import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import BulkUpload from '../components/common/BulkUpload';
import { useAuth } from '../context/AuthContext';

const defaultForm = {
  name: '',
  description: '',
  states: '',
  manager_id: '',
};

export default function Territories() {
  const { isAdmin, isManager } = useAuth();
  const canManage = isAdmin || isManager;

  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, mode: 'add', item: null });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [managers, setManagers] = useState([]);
  const [freezeConfirm, setFreezeConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/territories');
      setTerritories(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (canManage) {
      api.get('/users').then(r => {
        const all = r.data.data || [];
        setManagers(all.filter(u => u.role === 'admin' || u.role === 'manager'));
      }).catch(() => {});
    }
  }, [canManage]);

  const openAdd = () => {
    setForm(defaultForm);
    setError('');
    setModal({ open: true, mode: 'add', item: null });
  };

  const openEdit = (item) => {
    setForm({
      name: item.name || '',
      description: item.description || '',
      states: item.states || '',
      manager_id: item.manager_id || '',
    });
    setError('');
    setModal({ open: true, mode: 'edit', item });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Territory name is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.manager_id) delete payload.manager_id;
      if (modal.mode === 'add') {
        await api.post('/territories', payload);
      } else {
        await api.put(`/territories/${modal.item.id}`, payload);
      }
      setModal({ open: false, mode: 'add', item: null });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleFreeze = async () => {
    try {
      await api.delete(`/territories/${freezeConfirm.id}`);
      setFreezeConfirm(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to freeze.');
    }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Territories</h1>
          <p className="page-subtitle">Manage sales territories and their assigned managers</p>
        </div>
        {canManage && (
          <>
            <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Import
            </button>
            <button className="btn btn-primary" onClick={openAdd}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Territory
            </button>
          </>
        )}
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Territory Name</th>
                <th>Description</th>
                <th>States Covered</th>
                <th>Manager</th>
                <th>Customers</th>
                {canManage && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canManage ? 6 : 5}><div className="loading-overlay"><div className="spinner" /></div></td></tr>
              )}
              {!loading && territories.length === 0 && (
                <tr><td colSpan={canManage ? 6 : 5}>
                  <div className="empty-state"><p>No territories found. Add your first territory.</p></div>
                </td></tr>
              )}
              {!loading && territories.map(t => (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{t.name}</div>
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', maxWidth: 200 }}>
                    {t.description || '—'}
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem', maxWidth: 220 }}>
                    {t.states ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {t.states.split(',').map(s => s.trim()).filter(Boolean).map(s => (
                          <span key={s} style={{ background: 'var(--surface-2, #f3f4f6)', padding: '1px 7px', borderRadius: 10, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>
                    {t.manager_name || '—'}
                  </td>
                  <td>
                    <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{t.customer_count || 0}</span>
                  </td>
                  {canManage && (
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(t)}>
                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        {isAdmin && (
                          <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color: 'var(--error)' }} onClick={() => setFreezeConfirm(t)}>
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={modal.open}
        onClose={() => setModal({ open: false, mode: 'add', item: null })}
        title={modal.mode === 'add' ? 'Add Territory' : 'Edit Territory'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal({ open: false, mode: 'add', item: null })}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Save Territory'}
            </button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <div className="form-group">
          <label className="form-label">Territory Name *</label>
          <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. North Zone, Maharashtra" />
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-control" rows={2} value={form.description} onChange={e => f('description', e.target.value)} placeholder="Brief description of the territory" />
        </div>
        <div className="form-group">
          <label className="form-label">States Covered</label>
          <textarea className="form-control" rows={2} value={form.states} onChange={e => f('states', e.target.value)} placeholder="Comma-separated: Maharashtra, Goa, Gujarat" />
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Enter state names separated by commas</div>
        </div>
        <div className="form-group">
          <label className="form-label">Manager</label>
          <select className="form-control form-select" value={form.manager_id} onChange={e => f('manager_id', e.target.value)}>
            <option value="">-- No Manager --</option>
            {managers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
      </Modal>

      <Modal open={!!freezeConfirm} onClose={() => setFreezeConfirm(null)} title="Freeze Territory"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setFreezeConfirm(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={handleFreeze}>Freeze</button>
        </>}
      >
        <p>Freeze territory <strong>{freezeConfirm?.name}</strong>? It will become read-only.</p>
      </Modal>

      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Import Territories"
        uploadUrl="/territories/bulk"
        columns={[
          { key: 'name', label: 'Name', required: true },
          { key: 'description', label: 'Description' },
          { key: 'states', label: 'States' },
          { key: 'manager_id', label: 'Manager ID' },
        ]}
        onSuccess={load}
      />
    </div>
  );
}
